/**
 * Calcolo giorni lavorativi secondo il calendario italiano.
 * Esclude sabato, domenica e festività nazionali (incluse Pasquetta mobile).
 * Non considera festività locali/patronali.
 */

const atMidnight = (d: Date): Date => {
  const copy = new Date(d.getTime());
  copy.setHours(0, 0, 0, 0);
  return copy;
};

/** Domenica di Pasqua per l'anno dato (algoritmo di Gauss/Meeus). */
function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

/** Chiave "MM-DD" per una data, utile per confronti con festività fisse. */
const mmdd = (d: Date): string =>
  `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const FIXED_HOLIDAYS_MMDD = new Set<string>([
  '01-01', // Capodanno
  '01-06', // Epifania
  '04-25', // Festa della Liberazione
  '05-01', // Festa dei Lavoratori
  '06-02', // Festa della Repubblica
  '08-15', // Ferragosto
  '11-01', // Ognissanti
  '12-08', // Immacolata Concezione
  '12-25', // Natale
  '12-26', // Santo Stefano
]);

const pasquettaCache = new Map<number, string>();

function pasquettaMmdd(year: number): string {
  let key = pasquettaCache.get(year);
  if (key) return key;
  const easter = easterSunday(year);
  const pasquetta = new Date(easter.getTime());
  pasquetta.setDate(pasquetta.getDate() + 1);
  key = mmdd(pasquetta);
  pasquettaCache.set(year, key);
  return key;
}

/** true se la data è una festività nazionale italiana. */
export function isItalianHoliday(date: Date): boolean {
  const key = mmdd(date);
  if (FIXED_HOLIDAYS_MMDD.has(key)) return true;
  return key === pasquettaMmdd(date.getFullYear());
}

/** true se la data è un giorno lavorativo (no weekend, no festività nazionali). */
export function isBusinessDay(date: Date): boolean {
  const day = date.getDay();
  if (day === 0 || day === 6) return false;
  return !isItalianHoliday(date);
}

/**
 * Giorni lavorativi trascorsi tra due date (calendario italiano).
 * Convenzione: intervallo (start, end] — escluso il giorno di inizio, incluso il giorno di fine.
 * Coerente con la semantica di `daysBetween`: (Lun → Ven) = 4 giorni lavorativi.
 *
 * @returns numero di giorni lavorativi, o null se i parametri sono invalidi / end precede start
 */
export function businessDaysBetween(start: Date | null, end: Date | null): number | null {
  if (!start || !end) return null;
  const s = atMidnight(start);
  const e = atMidnight(end);
  if (e.getTime() < s.getTime()) return null;

  let count = 0;
  const cursor = new Date(s.getTime());
  cursor.setDate(cursor.getDate() + 1);
  while (cursor.getTime() <= e.getTime()) {
    if (isBusinessDay(cursor)) count++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}
